import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import Tooltip from '@/components/UI/Tooltip';
import { _isNil, _notNil } from '@/littledash';
import { DateInputUtils, DateUtils } from '@/utils/Date.utils';
import cn from 'classnames';
import type { ChangeEventHandler, FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  DataTableCellIndexCoordinate,
  DataTableCellSelectionChangeEvent,
  DataTableDirection,
} from '@/components/UI/DataTableV2/DataTable.model';
import { DataTableEvent } from '@/components/UI/DataTableV2/DataTable.model';
import styles from '../DataTableV2.module.scss';
import { useDataTable } from '../DataTableProvider';
import { useCellCoordinate } from '../hooks/useCellCoordinate';
import { useCellData } from '../hooks/useCellData';
import { useCellMouseHandlers } from '../hooks/useCellMouseHandlers';
import { useCellPositioning } from '../hooks/useCellPositioning';
import { useCellSelectionState } from '../hooks/useCellSelectionState';
import { useCellState } from '../hooks/useCellState';
import { useGlobalEventListenerAbort } from '../hooks/useGlobalEventListenerAbort';

interface DataTableTimestampCellProps {
  coordinate: DataTableCellIndexCoordinate;
  positionOffset: { x: number; y: number };
}

export const DataTableTimestampCell: FC<DataTableTimestampCellProps> = ({ coordinate, positionOffset }) => {
  const { dataTableService } = useDataTable();
  const datetimeInputRef = useRef<HTMLInputElement>(null);
  const [active, setActive] = useState(false);
  const [cellValueOverride, setCellValueOverride] = useState<string | null>(null);

  const handleAbort = useCallback(() => {
    setActive(false);
    setCellValueOverride(null);
  }, []);

  const { getSignal, abort } = useGlobalEventListenerAbort({ onAbort: handleAbort });

  const { idCoordinate, cellRef } = useCellCoordinate(coordinate);
  const { cellData } = useCellData(idCoordinate, cellRef);
  const { cellState, tooltip } = useCellState(idCoordinate, cellRef);
  const { selectionState } = useCellSelectionState(coordinate, idCoordinate);
  const { containerStyle } = useCellPositioning(positionOffset, active);

  const persistInputValue = (moveAfterPersistDirection?: DataTableDirection) => {
    setActive(() => false);
    setCellValueOverride(() => null);
    abort('completed');
    if (_notNil(idCoordinate) && _notNil(datetimeInputRef.current)) {
      const persistValue = DateInputUtils.localDateTimeToISODateTime(datetimeInputRef.current.value);
      if (persistValue === '' && _notNil(cellData?.value)) {
        dataTableService
          .updateCells([
            {
              column_id: idCoordinate.column,
              row_id: idCoordinate.row,
              value: null,
            },
          ])
          .then(() => {
            dataTableService.validate();
          });
      } else {
        if (persistValue !== cellData?.value) {
          dataTableService
            .updateCells([
              {
                column_id: idCoordinate.column,
                row_id: idCoordinate.row,
                value: persistValue,
              },
            ])
            .then(() => dataTableService.validate());
        }
      }
      if (
        !dataTableService.workflowService.workflowActionExit(idCoordinate.column) &&
        _notNil(moveAfterPersistDirection)
      ) {
        dataTableService.moveSelection(moveAfterPersistDirection);
      }
    }
  };

  const activatePicker = () => {
    if (!dataTableService.readonly) {
      const isoDateTime = DateUtils.isValidDateTime(cellData?.value)
        ? (cellData?.value ?? DateUtils.dateTimeNow())
        : DateUtils.dateTimeNow();
      if (_notNil(datetimeInputRef.current)) {
        setActive(true);
        datetimeInputRef.current.value = DateInputUtils.toLocalDateTime(isoDateTime);
        const signal = getSignal();
        datetimeInputRef.current.addEventListener('click', () => persistInputValue(), {
          once: true,
          capture: true,
          signal,
        });
        datetimeInputRef.current.addEventListener('blur', () => persistInputValue(), {
          once: true,
          capture: true,
          signal,
        });

        datetimeInputRef.current.addEventListener(
          'keyup',
          (event) => {
            switch (event.code) {
              case 'Enter': {
                persistInputValue(event.shiftKey ? 'UP' : 'DOWN');
                break;
              }
              case 'Escape': {
                persistInputValue();
                break;
              }
            }
          },
          {
            once: true,
            capture: true,
            signal,
          }
        );

        window.requestAnimationFrame(() => {
          datetimeInputRef?.current?.showPicker();
          datetimeInputRef?.current?.focus();
        });
      }
    }
  };

  // Custom timestamp insertion on cell activation (Enter key)
  useEffect(() => {
    if (_notNil(idCoordinate)) {
      const abortController = new AbortController();

      const cellSelectionChangeListener = (event: DataTableCellSelectionChangeEvent) => {
        const { from } = event.detail.selection;

        if (event.detail.activate && from.column === coordinate.column && from.row === coordinate.row) {
          if (_notNil(idCoordinate) && _isNil(event.detail.content)) {
            window.requestAnimationFrame(() => {
              dataTableService
                .updateCells([
                  {
                    column_id: idCoordinate.column,
                    row_id: idCoordinate.row,
                    value: new Date().toISOString(),
                  },
                ])
                .then(() => dataTableService.validate());
              if (!dataTableService.workflowService.workflowActionExit(idCoordinate.column)) {
                dataTableService.moveSelection(event.detail.options?.direction ?? 'DOWN');
              }
            });
          }
        }
      };

      dataTableService.subscribe(
        DataTableEvent.CellSelectionChange,
        cellSelectionChangeListener,
        abortController.signal
      );

      return () => {
        abortController.abort();
      };
    }
  }, [coordinate, idCoordinate, cellData, dataTableService]);

  // Mouse interaction handlers
  const { handleMouseDown, handleMouseEnter, handleMouseUp } = useCellMouseHandlers(coordinate);

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const value = DateInputUtils.localDateTimeToISODateTime(event?.target?.value ?? '');
    setCellValueOverride(value === '' ? null : value);
  };

  const cellValue = cellValueOverride ?? cellData?.value;
  const hasGroupBorder = dataTableService.rightBorderType(coordinate.column) === 'group-separator';

  return (
    <div
      className={cn(styles.CellContainer, { [styles.groupBorder]: hasGroupBorder })}
      style={containerStyle}
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseUp={handleMouseUp}
      onDoubleClick={activatePicker}
    >
      {cellState === 'Loading' ? (
        <div className={cn(styles.Cell, styles.Loading)} />
      ) : (
        <Tooltip render={tooltip}>
          <div
            className={cn(styles.Cell, styles[selectionState], { [styles.Active]: active }, styles[cellState])}
            data-state={cellState}
          >
            <span className={styles.TimestampValue}>
              {_isNil(cellValue) ? (
                <>Add Timestamp</>
              ) : (
                <DateTimeRenderer value={cellValue} defaultResponse={cellValue} />
              )}
            </span>
            <input
              className={styles.TimestampInput}
              style={{ visibility: active ? 'visible' : 'hidden' }}
              ref={datetimeInputRef}
              type="datetime-local"
              onChange={handleInputChange}
            />
          </div>
        </Tooltip>
      )}
    </div>
  );
};
